
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

// Layouts
import MainLayout from './components/layout/MainLayout';

// Eager: the pages a first visit lands on. Everything else is code-split so
// the home page does not pay for charts, Stripe, the gallery editor, etc.
import HomePage from './components/common/HomePage';
import LoginForm from './features/auth/components/LoginForm';
import RegisterForm from './features/auth/components/RegisterForm';
import PricingPage from './features/subscription/components/PricingPage';

// Protected Route Wrapper
import ProtectedRoute from './components/common/ProtectedRoute';

// Auth (secondary)
const ForgotPasswordForm = lazy(() => import('./features/auth/components/ForgotPasswordForm'));
const ResetPasswordForm = lazy(() => import('./features/auth/components/ResetPasswordForm'));
const VerifyEmailPage = lazy(() => import('./features/auth/components/VerifyEmailPage'));

// Account
const Dashboard = lazy(() => import('./features/account/components/Dashboard'));
const ApiKeysList = lazy(() => import('./features/account/components/ApiKeysList'));
const UserProfileForm = lazy(() => import('./features/account/components/UserProfileForm'));
const UsagePage = lazy(() => import('./features/account/components/UsagePage'));

// Payment
const PaymentPage = lazy(() => import('./features/payment/components/PaymentPage'));
const PaymentHistoryList = lazy(() => import('./features/payment/components/PaymentHistoryList'));

// Documentation
const DocumentationHome = lazy(() => import('./features/documentation/components/DocumentationHome'));

// Gallery
const GalleryBrowsePage = lazy(() => import('./features/gallery/components/GalleryBrowsePage'));
const GalleryItemDetail = lazy(() => import('./features/gallery/components/GalleryItemDetail'));
const GallerySubmitForm = lazy(() => import('./features/gallery/components/GallerySubmitForm'));

// Reviews
const ReviewSubmitForm = lazy(() => import('./features/reviews/components/ReviewSubmitForm'));

// Sessions
const RemoteSessionsPage = lazy(() => import('./features/sessions/components/RemoteSessionsPage'));

// Redeem (convention coupon QR landing)
const RedeemLanding = lazy(() => import('./features/redeem').then((m) => ({ default: m.RedeemLanding })));

// Surprise (shareable "magic box" coupon link)
const SurpriseLanding = lazy(() => import('./features/surprise').then((m) => ({ default: m.SurpriseLanding })));

// Static pages
const NotFoundPage = lazy(() => import('./components/common/NotFoundPage'));
const AboutPage = lazy(() => import('./components/common/AboutPage'));
const ContactPage = lazy(() => import('./components/common/ContactPage'));
const TermsPage = lazy(() => import('./components/common/TermsPage'));
const PrivacyPage = lazy(() => import('./components/common/PrivacyPage'));
const TrainingPage = lazy(() => import('./components/common/TrainingPage'));

const RouteFallback: React.FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
    <CircularProgress />
  </Box>
);

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<MainLayout maxWidth={false}><HomePage /></MainLayout>} />

        {/* Auth Routes */}
        <Route path="/login" element={
          <MainLayout maxWidth="xs">
            <LoginForm />
          </MainLayout>
        } />

        <Route path="/register" element={
          <MainLayout maxWidth="sm">
            <RegisterForm />
          </MainLayout>
        } />

        <Route path="/forgot-password" element={
          <MainLayout maxWidth="xs">
            <ForgotPasswordForm />
          </MainLayout>
        } />

        <Route path="/reset-password/:token" element={
          <MainLayout maxWidth="xs">
            <ResetPasswordForm />
          </MainLayout>
        } />

        {/* Email verification — the destination for /verify-email?token=...
            links in account-verification emails. */}
        <Route path="/verify-email" element={
          <MainLayout maxWidth="xs">
            <VerifyEmailPage />
          </MainLayout>
        } />

        {/* Pricing - Public */}
        <Route path="/pricing" element={
          <MainLayout maxWidth="lg">
            <PricingPage />
          </MainLayout>
        } />

        {/* Convention coupon QR landing — public. Both forms render the
            same component; the :code variant is what the printed QR
            codes point at. The bare /redeem form lets someone type the
            URL and enter the code manually. */}
        <Route path="/redeem" element={
          <MainLayout maxWidth="sm">
            <RedeemLanding />
          </MainLayout>
        } />
        <Route path="/redeem/:code" element={
          <MainLayout maxWidth="sm">
            <RedeemLanding />
          </MainLayout>
        } />

        {/* Surprise "magic box" — one shared link per batch. Rendered
            full-bleed (no MainLayout chrome) for the immersive reveal. */}
        <Route path="/surprise/:token" element={<SurpriseLanding />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/api-keys" element={
          <ProtectedRoute>
            <MainLayout>
              <ApiKeysList />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/profile" element={
          <ProtectedRoute>
            <MainLayout>
              <UserProfileForm />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/payment" element={
          <ProtectedRoute>
            <MainLayout>
              <PaymentPage />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/payments-history" element={
          <ProtectedRoute>
            <MainLayout>
              <PaymentHistoryList />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/usage" element={
          <ProtectedRoute>
            <MainLayout>
              <UsagePage />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Remote Sessions - Protected */}
        <Route path="/sessions" element={
          <ProtectedRoute>
            <MainLayout>
              <RemoteSessionsPage />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Gallery Routes */}
        <Route path="/gallery" element={<MainLayout maxWidth={false}><GalleryBrowsePage /></MainLayout>} />
        <Route path="/gallery/submit" element={
          <ProtectedRoute><MainLayout><GallerySubmitForm /></MainLayout></ProtectedRoute>
        } />
        <Route path="/gallery/:id/edit" element={
          <ProtectedRoute><MainLayout><GallerySubmitForm /></MainLayout></ProtectedRoute>
        } />
        <Route path="/gallery/:id" element={<MainLayout><GalleryItemDetail /></MainLayout>} />

        {/* Reviews Submit - Protected */}
        <Route path="/reviews/submit" element={
          <ProtectedRoute><MainLayout><ReviewSubmitForm /></MainLayout></ProtectedRoute>
        } />

        {/* Documentation Routes */}
        <Route path="/docs" element={
          <MainLayout>
            <DocumentationHome />
          </MainLayout>
        } />

        <Route path="/docs/:section" element={
          <MainLayout>
            <DocumentationHome />
          </MainLayout>
        } />

        {/* Static Pages */}
        <Route path="/about" element={
          <MainLayout>
            <AboutPage />
          </MainLayout>
        } />

        <Route path="/contact" element={
          <MainLayout>
            <ContactPage />
          </MainLayout>
        } />

        <Route path="/terms" element={
          <MainLayout>
            <TermsPage />
          </MainLayout>
        } />

        <Route path="/privacy" element={
          <MainLayout>
            <PrivacyPage />
          </MainLayout>
        } />

        <Route path="/training" element={
          <MainLayout maxWidth={false}>
            <TrainingPage />
          </MainLayout>
        } />

        {/* Error/Not Found */}
        <Route path="/404" element={
          <MainLayout>
            <NotFoundPage />
          </MainLayout>
        } />

        {/* Redirect any unmatched routes to 404 */}
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
